import { z } from "zod";
import { EquipmentKind, MovementPattern, MuscleRegion } from "./muscle.js";
import { LocalizedText } from "../localizedText.js";
import { localizedOptionalText } from "./localizedOptionalText.js";
import { MuscleContrib } from "../muscleCatalog.js";
import { slugString } from "./slug.js";

const MuscleSlug = slugString(64);

// Multilingual short description shown in the catalog editor and as a
// "?" hint in the workout logger. Every locale is independently optional
// so a coach who only writes EN doesn't have to fill blanks; the renderer
// falls back through the user's locale → EN → first available. The 800
// char ceiling keeps the hint readable on a phone — anything longer
// belongs in a separate tutorial, not on the row.
const LocalizedDescription = localizedOptionalText(800);
export type LocalizedDescription = z.infer<typeof LocalizedDescription>;

// Bodyweight exercises track {reps, bw, extra?}; weighted track {reps, weight}.
// hasP1RM enables percentage-of-1RM programming for that exercise — see SL
// memory on omit-vs-zero semantics: present-0 is meaningful, not absent.
//
// `primaryMuscle` is the legacy free-text field kept for read-side
// compatibility with rows created before the taxonomy import. New code
// reads `primary[]` first and falls back to `primaryMuscle` only when
// the array is empty. Treat it as deprecated for new writes.
//
// `source` distinguishes the three catalog tiers (decided 2026-05-07):
//   - "stock"         — admin-curated, lives in CATALOG_PK. Read-only for
//                       non-admin users.
//   - "user"          — user-curated, lives in USER#<sub>. Manually authored
//                       or promoted from auto_imported by that user.
//   - "auto_imported" — created by the CSV log importer in USER#<sub>
//                       when no high-confidence match exists in the
//                       catalog. Usable for logging, flagged as draft. The
//                       cold-path review agent (T12.2 future) enriches
//                       and the user (or admin, on consent) promotes.
// Absence on a stored row is treated as "stock" by the API for
// backward-compat with rows written before this field existed.
//
// `curatedBy` is the sub of the admin who authored a stock row. Optional
// to keep the door open for multi-admin without a schema migration; on
// rows lacking it, the API treats the row as authored by the bootstrap
// admin. Only meaningful on `source: "stock"` rows; user/auto_imported
// rows are owned by the partition (USER#<sub>) and the field is omitted.
//
// `ownerSub` is the canonical owner sub across all three tiers (added
// 2026-05-15 per ROLES_AUDIT Tier 3.1). For stock rows it equals
// `curatedBy` (the admin who authored the row); for user/auto_imported
// rows it equals the partition sub. Surfacing the field on the row
// itself lets row-level ACL (coach-shared exercise visibility, owner
// edit gates) read without a partition lookup. Optional on read for
// back-compat with rows written before this field existed — fall back
// to `curatedBy` on stock rows or to the partition sub on user rows.
export const Exercise = z.object({
  id: z.string().min(1),
  slug: slugString(64),
  // Localized name. EN is the deterministic canonical (required), other
  // locales optional. Catalog search/sort/dedup normalises against name.en
  // (stable across locale switches); rendering picks the user's locale
  // via pickLocalizedText() and falls back to EN. Policy flipped
  // 2026-05-22 from plain-string canonical-EN after Antenore's family
  // (FR/DE/RU speakers) reported the EN-only experience as alienating.
  // See [[feedback_exercise_names_canonical_english]] for the prior
  // rationale (now superseded).
  name: LocalizedText,
  isBodyweight: z.boolean(),
  hasP1RM: z.boolean(),
  source: z.enum(["stock", "user", "auto_imported"]).optional(),
  curatedBy: z.string().min(1).max(128).optional(),
  ownerSub: z.string().min(1).max(128).optional(),
  primaryMuscle: z.string().min(1).max(40).optional(),
  primary: z.array(MuscleSlug).max(8).optional(),
  secondary: z.array(MuscleSlug).max(12).optional(),
  // Per-muscle recruitment map (slice #4). The volume aggregator reads
  // this single field to compute volumeByMuscle; `primary` / `secondary`
  // stay on the row for catalog search / filters but no longer enter
  // the volume math. Optional during transition — Phase 3 backfills the
  // ~269 catalog rows via agents (one-shot write); rows still lacking
  // the field on read fall back to the legacy primary[0]-equal-weighting
  // path until backfilled. See `muscleCatalog.ts` + `next/muscle-breakdown.md`.
  muscleContrib: MuscleContrib.optional(),
  region: MuscleRegion.optional(),
  movementPattern: MovementPattern.optional(),
  equipment: EquipmentKind.optional(),
  // Laterality VALUE: true = one side logged, counts ×2 (split squat,
  // single-arm row, two-dumbbell work); false / absent = bilateral, the
  // entered weight is the total load (×1).
  unilateral: z.boolean().optional(),
  // Whether the athlete may flip the laterality per session. Most lifts
  // are intrinsically one or the other (a back squat can't be made
  // unilateral; a lunge is always unilateral) → false/absent, NO chip,
  // the value is fixed. Only genuinely-ambiguous lifts (dumbbell work you
  // might do two-arm or single-arm) set true, which surfaces the BI/UNI
  // toggle chip in the Logger. Decided 2026-05-26.
  lateralityModifiable: z.boolean().optional(),
  // Fraction of bodyweight effectively moved per rep, expressed as a
  // percentage (0–150). Drives the volume formula for `kind: bodyweight`
  // sets: a 20-rep pull-up at bw=80kg counts as 20×80×1.00 = 1600 kg;
  // 20 push-ups count as 20×80×0.64 = ~1024 kg. Standard biomechanical
  // references (ACE study, Bret Contreras): pull-up/dip/chin-up = 100,
  // push-up ≈ 64, sit-up ≈ 55, crunch ≈ 30, inverted row ≈ 50, back
  // extension / reverse hyperextension ≈ 35–40. For machine-assisted
  // variants set ~70 as a static average; future logs can refine via a
  // negative `extra` value on the LogSet. Isometrics (plank, hold) use
  // the same pct as their dynamic analogue × 1 rep — duration-based
  // effort is a future enhancement. Absent → default 100 in setVolumeKg.
  bodyweightLoadPct: z.number().min(0).max(150).optional(),
  // Free-form tags. Slug-format so they're URL-safe and indexable; max 20
  // per exercise to keep the chip UI manageable. Tags coexist with the
  // structured fields above: enums drive the logger logic (carry → distance,
  // bodyweight → extra), tags drive the report axes — group volume by tag,
  // filter Progress by tag, etc. The suggested starter set is derived from
  // the structural fields client-side; the user can add anything else.
  tags: z.array(slugString(64)).max(20).optional(),
  // Display-name aliases. Free-text (not slug) so a coach can list the
  // common variants users type ("OVH Press", "Military Press" for an
  // Overhead Press row). The CSV importer's name matcher consults this
  // list alongside the canonical name, so adding "Cable Curl - Rope" as
  // an alias on Cable Curl makes future imports auto-map without a
  // manual pick. Aliases live on the catalog row itself (single-tenant
  // family app: one shared catalog), not per-user.
  aliases: z
    .array(z.string().min(1).max(120))
    .max(20)
    .optional(),
  // Coach-authored short description per locale. Optional in every shape:
  // the bundled taxonomy ships descriptions for the 88 default exercises,
  // but a coach-created custom row can save without one and add the copy
  // later. Empty objects round-trip as absent (DDB removes the attribute
  // — see the API patch handler).
  description: LocalizedDescription.optional(),
  // Default rest between sets in seconds. Drives the logger's rest timer;
  // user always overrides per-set. 0..600 covers sub-minute accessory work
  // up to 10-minute heavy-single rests. Absent = fall back to the
  // app-wide default (90s). Compound lifts ship pre-seeded; accessories
  // start unset.
  defaultRestSec: z.number().int().min(0).max(600).optional(),
  // Time-based (isometric) exercise: logged by DURATION (seconds held), not
  // reps×weight. Planks, wall sits, bird-dog holds and the rest of the
  // physiotherapy isometric work (inbox 3dd20b1c). Drives the logger to show a
  // duration field + the count-up/down hold timer instead of reps/weight. The
  // set still stores its seconds in the existing LogSet.duration field (kind
  // 'timed'). Absent/false = the normal reps×weight (or cardio) path.
  isTimeBased: z.boolean().optional(),
  // Default countdown time in seconds for any duration-logged exercise — the
  // twin of defaultRestSec, seeded into the count-down/up timer when a set is
  // started. 0..3600 covers a few-second tendon hold up to a long wall sit or
  // a 40-minute cardio block. Absent = fall back to the app-wide default.
  // Meaningful for BOTH kinds that log a duration: isTimeBased holds AND
  // movementPattern==='cardio' rows (TM-5 — cardio gets the same start-timer
  // as holds, with the same per-exercise default the gear ⚙ writes here).
  defaultDurationSec: z.number().int().min(0).max(3600).optional(),
  // Specialty bar weight in kg. Curl bar 7, trap bar 11, landmine ~10.
  // When set, the plate calculator seeds the bar dropdown to this value
  // unless the user has already picked a specific bar for that exercise.
  // Absent = no exercise-specific default, the modal falls back to the
  // user-selected bar dropdown.
  defaultBarKg: z.number().min(0).max(50).optional(),
  // How the bar accepts plates. 'standard' = symmetric loading on both
  // ends (the common case). 'landmine' = one end is anchored to the
  // floor pivot, only the loaded end gets plates — the calculator
  // shows a single side instead of pairs and the per-side math drops
  // out (target - bar = total plates loaded, not per side). Absent
  // reads as 'standard' for back-compat with the existing 242 catalog.
  barLoading: z.enum(["standard", "landmine"]).optional(),
  // Global per-exercise setup cue. Shipped behaviour: it is its own
  // labelled block («Coach note») in the `?` tip modal, shown ALONGSIDE
  // the plan/program cue rather than being overridden by it — the
  // single-winner resolver this comment used to name was specified but
  // never built, and was deleted 2026-08-01 (spec §7.3 SUPERSEDED). So
  // an athlete on a plan sees both layers, deliberately. Single
  // string (not LocalizedText) — user-typed setup cues stay in the
  // creator's locale per feedback_localized_text_policy. 500 char
  // ceiling matches the other note fields in the stack.
  pinnedNote: z.string().max(500).optional(),
  // Deprecate-without-orphaning flag (2026-06-19). true = hide the exercise
  // from the add-picker (it's a junk / retired entry, e.g. an SL joke import)
  // while KEEPING the row so past sets that reference its slug still resolve
  // their name (feedback_no_denormalized_display_snapshots — names resolve at
  // render from the catalog, so deleting the row would break history). Absent
  // / false = a normal, pickable exercise.
  archived: z.boolean().optional(),
  createdAt: z.iso.datetime(),
  // API-3 — last write to this row. OPTIONAL because it is younger than the
  // catalogue: rows seeded before it exist without one, and a missing value
  // means "never touched since seeding", not "unknown". Server-owned, so it is
  // omitted from NewExercise below and a client that sends one is rejected by
  // the strict PATCH schema. Until 2026-08-22 it was written only by migration
  // scripts and not declared here at all, which meant this schema STRIPPED it
  // out of every API response — the field existed in DynamoDB and no caller
  // could see it.
  updatedAt: z.iso.datetime().optional(),
});
export type Exercise = z.infer<typeof Exercise>;

export const NewExercise = Exercise.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type NewExercise = z.infer<typeof NewExercise>;
